/* ClaudeFlowDemo: a small looping "you ask, Claude works, you get the result"
   demo for the Claude for Beginners learn cards (Esther, 2026-10-30).

   The pattern follows how claude.com explains its product (a prompt types in,
   Claude's steps tick through, the finished work appears), drawn in our own
   Vivid oranges and type. No Anthropic assets, logos or marks: the active
   cease-and-desist bars the asterisk, so the send button is a plain arrow.

   Plays only while on screen. Reduced motion gets the finished state, still. */

import { useEffect, useRef, useState } from "react";

export interface ClaudeFlowScript {
  /** Small label over the prompt, e.g. the Project the chat lives in. */
  context: { label: string; files: string[]; fly?: boolean };
  /** Connected apps: their logos fly in from around the stage and dock in the
   *  context row, in place of the file chips. */
  apps?: { name: string; src: string }[];
  /** Optional app logo per step, shown in place of the tick's label lead. */
  stepIcons?: (string | null)[];
  prompt: string;
  steps: string[];
  result: { title: string; lines: string[] };
  /** Screen-reader summary of the whole demo. */
  label: string;
}

type Phase = "typing" | "steps" | "result";

/* Where each app logo starts before it flies into the context row, as offsets
   in the demo's own width (cqw) so the move scales with the stage. */
const APP_FROM: [string, string][] = [
  ["-30cqw", "34cqw"],
  ["-6cqw", "52cqw"],
  ["18cqw", "46cqw"],
  ["32cqw", "26cqw"],
  ["26cqw", "-6cqw"],
];

const TYPE_MS = 34;
const STEP_MS = 900;
const HOLD_MS = 4200;

export function ClaudeFlowDemo({
  script,
  onDone,
  tone = "paper",
  instant = false,
}: {
  script: ClaudeFlowScript;
  /** Called when a run finishes; when set, the demo hands off instead of looping. */
  onDone?: () => void;
  /** "paper" = light stage (for orange cards); "sunset" = orange stage (for paper sections). */
  tone?: "paper" | "sunset" | "monet";
  /** Show the finished state at once (prompt, ticks and result), no typing or
   *  loop. Claude for Beginners' curriculum (Esther, 2026-11-02). */
  instant?: boolean;
}) {
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const root = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [run, setRun] = useState(0);
  const [chars, setChars] = useState(0);
  const [stepsDone, setStepsDone] = useState(0);
  const [phase, setPhase] = useState<Phase>("typing");
  const [appsIn, setAppsIn] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const el = root.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (reduced || instant) {
      setChars(script.prompt.length);
      setStepsDone(script.steps.length);
      setPhase("result");
      setAppsIn(true);
      return;
    }
    if (!visible) return;
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));
    setChars(0);
    setStepsDone(0);
    setPhase("typing");
    setAppsIn(false);
    at(120, () => setAppsIn(true));
    let t = script.apps || script.context.fly ? 1500 : 500;
    for (let i = 1; i <= script.prompt.length; i++) at((t += TYPE_MS), () => setChars(i));
    at((t += 500), () => setPhase("steps"));
    script.steps.forEach((_, i) => at((t += STEP_MS), () => setStepsDone(i + 1)));
    at((t += 700), () => setPhase("result"));
    at((t += HOLD_MS), () => (doneRef.current ? doneRef.current() : setRun((r) => r + 1)));
    return () => timers.forEach(clearTimeout);
  }, [visible, reduced, run, script, instant]);

  const typing = phase === "typing";

  return (
    <div ref={root} className="cfd" data-tone={tone} role="img" aria-label={script.label}>
      <div className="cfd-stage" aria-hidden="true">
        <span className="cfd-back cfd-back-2" />
        <span className="cfd-back cfd-back-1" />

        <div className="cfd-panel" data-phase={phase}>
          <div className="cfd-chat" data-apps-in={appsIn ? "true" : "false"}>
            <div className="cfd-context">
              <span className="cfd-folder" />
              <span className="cfd-context-label">{script.context.label}</span>
              <span className="cfd-files">
                {script.apps
                  ? script.apps.map((a, i) => (
                      <span
                        key={a.name}
                        className="cfd-app"
                        style={{ ["--dx" as string]: APP_FROM[i % APP_FROM.length][0], ["--dy" as string]: APP_FROM[i % APP_FROM.length][1], transitionDelay: `${i * 140}ms` }}
                      >
                        <img src={a.src} alt="" />
                        <span>{a.name}</span>
                      </span>
                    ))
                  : script.context.files.map((f, i) => (
                      <span
                        key={f}
                        className={script.context.fly ? "cfd-file cfd-file-fly" : "cfd-file"}
                        style={script.context.fly ? { ["--dx" as string]: APP_FROM[(i + 1) % APP_FROM.length][0], ["--dy" as string]: APP_FROM[(i + 1) % APP_FROM.length][1], transitionDelay: `${i * 220}ms` } : undefined}
                      >
                        {script.context.fly && <span className="cfd-file-icon" />}
                        {f}
                      </span>
                    ))}
              </span>
            </div>
            <div className="cfd-input">
              <p className="cfd-prompt">
                {script.prompt.slice(0, chars)}
                {typing && <span className="cfd-caret" />}
              </p>
              <span className="cfd-send" data-on={chars > 0 ? "true" : "false"}>
                <svg viewBox="0 0 16 16" width="12" height="12"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
              </span>
            </div>
          </div>

          <ol className="cfd-steps" data-show={phase !== "typing" ? "true" : "false"}>
            {script.steps.map((s, i) => (
              <li key={s} className="cfd-step" data-state={i < stepsDone ? "done" : i === stepsDone && phase === "steps" ? "now" : "todo"}>
                <span className="cfd-tick" />
                {script.stepIcons?.[i] && <img className="cfd-step-icon" src={script.stepIcons[i]!} alt="" />}
                {s}
              </li>
            ))}
          </ol>

          <div className="cfd-result" data-show={phase === "result" ? "true" : "false"}>
            <div className="cfd-doc-bar">
              <span /><span /><span />
            </div>
            <div className="cfd-doc">
              <p className="cfd-doc-title">{script.result.title}</p>
              {script.result.lines.map((l) => (
                <p key={l} className="cfd-doc-line">{l}</p>
              ))}
              <span className="cfd-doc-rule" />
              <span className="cfd-doc-rule cfd-doc-rule-short" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
